import { dateOnlyInTz } from './timezone';
import { loadBundledNdisCatalogueSeed, ndisItemToMdmRow } from './ndis';
import {
  OFFICIAL_SAH_SERVICE_CATALOGUE,
  SAH_SERVICE_LIST_SOURCE,
  sahContributionCategoryForServiceAtDate,
} from './sah-service-list';

export const MDM_SERVICE_CATALOGUES_FLAG = 'MDM_SERVICE_CATALOGUES_ENABLED';
export const MDM_SERVICE_CATALOGUE_TTL_MS = 60_000;

export type MdmServiceProgram = 'SAH' | 'RAC' | 'CHSP' | 'NDIS' | 'PRIVATE';
export type MdmServiceCategory =
  | 'CLINICAL_CARE'
  | 'INDEPENDENCE'
  | 'EVERYDAY_LIVING'
  | 'AT_HM'
  | 'CARE_MANAGEMENT'
  | 'OTHER';
export type MdmServiceUnitType = 'HOUR' | 'VISIT' | 'WEEK' | 'KM';
export type SahUiServiceCategory = 'clinical' | 'independence' | 'everyday_living';
export type SahUiUnitType = 'hours' | 'trip' | 'item' | 'bill' | 'voucher' | 'meal';
export type SahFundingSourceCode = 'ON' | 'CM' | 'AT' | 'HM' | 'RC' | 'EL';

export interface MdmServiceCatalogueRow {
  serviceCode: string;
  program: MdmServiceProgram | string;
  category: MdmServiceCategory | string;
  displayName: string;
  description: string | null;
  defaultUnitType: MdmServiceUnitType | string;
  workerClassificationMinimum: string | null;
  isActive: boolean;
  effectiveFrom: string;
  effectiveTo: string | null;
  tenantId?: string | null;
  tenantDisplayName?: string | null;
  tenantDescription?: string | null;
  isEnabled?: boolean;
  resolvedDisplayName?: string;
  resolvedDescription?: string | null;
  serviceType?: string;
  serviceCategory?: string;
  unitType?: SahUiUnitType | string;
  fundingSourceCode?: SahFundingSourceCode | string;
  sortOrder?: number;
  serviceTypeLabel?: string;
  sourceUrl?: string;
  sourcePublicationDate?: string;
  sourceSha256?: string;
  clinicalCategoryFrom?: string;
}

export interface MdmServiceCatalogueRpcRow {
  service_code: string;
  program: string;
  category: string;
  display_name: string;
  description: string | null;
  default_unit_type: string;
  worker_classification_minimum: string | null;
  is_active: boolean;
  effective_from: string;
  effective_to: string | null;
  tenant_id?: string | null;
  tenant_display_name?: string | null;
  tenant_description?: string | null;
  is_enabled?: boolean;
  resolved_display_name?: string | null;
  resolved_description?: string | null;
  service_type?: string | null;
  service_category?: string | null;
  unit_type?: string | null;
  funding_source_code?: string | null;
  sort_order?: number | null;
  service_type_label?: string | null;
  source_url?: string | null;
  source_publication_date?: string | null;
  source_sha256?: string | null;
  clinical_category_from?: string | null;
}

export interface MdmServiceCatalogueOption {
  value: string;
  label: string;
  serviceCode: string;
  serviceCategory: SahUiServiceCategory;
  unitType: SahUiUnitType;
  fundingSourceCode: SahFundingSourceCode;
}

export interface SahServiceDefaults {
  serviceCode: string | null;
  serviceType: string;
  serviceCategory: SahUiServiceCategory;
  unitType: SahUiUnitType;
  fundingSourceCode: SahFundingSourceCode;
  displayName: string | null;
}

export interface GetServiceCatalogueOptions {
  tenantId?: string | null;
  /**
   * Omit to default to SAH. Pass `null` to mean "this plan type maps to no
   * catalogue programme" — that is answered with an empty result, never with
   * the Support at Home catalogue.
   */
  program?: MdmServiceProgram | string | null;
  atDate?: string;
  enabled?: boolean;
  ttlMs?: number;
  nowMs?: number;
  fetcher?: MdmServiceCatalogueFetcher;
  cacheKey?: string;
}

export interface MdmServiceCatalogueLookupResult {
  enabled: boolean;
  source: 'mdm' | 'fallback';
  rows: MdmServiceCatalogueRow[];
  program?: MdmServiceProgram | string | null;
  error?: string;
}

export type MdmServiceCatalogueFetcher = (args: {
  tenantId?: string | null;
  program: MdmServiceProgram | string;
  atDate: string;
}) => Promise<readonly (MdmServiceCatalogueRow | MdmServiceCatalogueRpcRow)[]>;

const LEGACY_SERVICE_CATEGORY_DEFAULTS: Record<string, SahUiServiceCategory> = {
  care_management: 'clinical',
  nursing: 'clinical',
  allied_health: 'clinical',
  podiatry: 'clinical',
  physiotherapy: 'clinical',
  occupational_therapy: 'clinical',
  speech_pathology: 'clinical',
  dietetics: 'clinical',
  psychology: 'clinical',
  personal_care: 'independence',
  therapeutic_services_independent_living: 'independence',
  cognitive_behavioural_support: 'independence',
  continence_management_non_clinical: 'independence',
  assistive_technology: 'independence',
  home_modification: 'independence',
  equipment: 'independence',
  domestic_assistance: 'everyday_living',
  social_support: 'independence',
  transport: 'independence',
  meal_preparation: 'everyday_living',
  respite: 'everyday_living',
  garden_maintenance: 'everyday_living',
  registered_nurse: 'clinical',
  enrolled_nurse: 'clinical',
  nursing_assistant: 'clinical',
  nursing_care_consumables: 'clinical',
  aboriginal_torres_strait_islander_health_practitioner: 'clinical',
  aboriginal_torres_strait_islander_health_worker: 'clinical',
  allied_health_therapy_assistant: 'clinical',
  counsellor_psychotherapist: 'clinical',
  dietitian_nutritionist: 'clinical',
  exercise_physiologist: 'clinical',
  music_therapist: 'clinical',
  occupational_therapist: 'clinical',
  physiotherapist: 'clinical',
  psychologist: 'clinical',
  social_worker: 'clinical',
  speech_pathologist: 'clinical',
  prescribed_nutrition: 'clinical',
  home_support_care_management: 'clinical',
  home_support_restorative_care_management: 'clinical',
  assistance_self_care_activities_daily_living: 'independence',
  assistance_self_administration_medication: 'independence',
  group_social_support: 'independence',
  individual_social_support: 'independence',
  accompanied_activities: 'independence',
  cultural_support: 'independence',
  digital_education_support: 'independence',
  assistance_maintain_personal_affairs: 'independence',
  expenses_maintain_personal_affairs: 'independence',
  acupuncturist: 'independence',
  chiropractor: 'independence',
  diversional_therapist: 'independence',
  remedial_masseuse: 'independence',
  art_therapist: 'independence',
  osteopath: 'independence',
  respite_care: 'independence',
  direct_transport_driver_car_provided: 'independence',
  indirect_transport_taxi_rideshare_vouchers: 'independence',
  general_house_cleaning: 'everyday_living',
  laundry_services: 'everyday_living',
  shopping_assistance: 'everyday_living',
  gardening: 'everyday_living',
  assistance_home_maintenance_repairs: 'everyday_living',
  expenses_home_maintenance_repairs: 'everyday_living',
  meal_delivery: 'everyday_living',
};

const LEGACY_UNIT_TYPE_DEFAULTS: Record<string, SahUiUnitType> = {
  transport: 'trip',
  meal_preparation: 'meal',
  assistive_technology: 'item',
  home_modification: 'item',
  home_modifications: 'item',
  equipment: 'item',
  nursing_care_consumables: 'item',
  prescribed_nutrition: 'item',
  expenses_maintain_personal_affairs: 'item',
  direct_transport_driver_car_provided: 'trip',
  indirect_transport_taxi_rideshare_vouchers: 'voucher',
  expenses_home_maintenance_repairs: 'item',
  meal_delivery: 'meal',
};

const LEGACY_FUNDING_CODE_DEFAULTS: Record<string, SahFundingSourceCode> = {
  care_management: 'CM',
  home_support_care_management: 'CM',
  home_support_restorative_care_management: 'RC',
  assistive_technology: 'AT',
  equipment: 'AT',
  home_modification: 'HM',
  home_modifications: 'HM',
};

const serviceCatalogueCache = new Map<
  string,
  { expiresAt: number; result: MdmServiceCatalogueLookupResult }
>();

function defaultMdmUnitTypeForSahUnit(unitType: SahUiUnitType): MdmServiceUnitType {
  switch (unitType) {
    case 'hours':
      return 'HOUR';
    case 'bill':
      return 'WEEK';
    case 'trip':
    case 'item':
    case 'voucher':
    case 'meal':
      return 'VISIT';
  }
}

function officialSahFallbackRow(
  item: (typeof OFFICIAL_SAH_SERVICE_CATALOGUE)[number],
  atDate: string,
): MdmServiceCatalogueRow {
  const serviceCategory = sahContributionCategoryForServiceAtDate(item, atDate);
  return {
    serviceCode: item.serviceCode,
    program: 'SAH',
    category: item.mdmCategory,
    displayName: item.displayName,
    description: `${SAH_SERVICE_LIST_SOURCE.title}: ${item.serviceTypeLabel}.`,
    defaultUnitType: defaultMdmUnitTypeForSahUnit(item.unitType),
    workerClassificationMinimum: null,
    isActive: true,
    effectiveFrom: SAH_SERVICE_LIST_SOURCE.effectiveFrom,
    effectiveTo: null,
    isEnabled: true,
    resolvedDisplayName: item.displayName,
    resolvedDescription: `${SAH_SERVICE_LIST_SOURCE.title}: ${item.serviceTypeLabel}.`,
    serviceType: item.serviceType,
    serviceCategory,
    unitType: item.unitType,
    fundingSourceCode: item.fundingSourceCode,
    sortOrder: item.sortOrder,
    serviceTypeLabel: item.serviceTypeLabel,
    sourceUrl: SAH_SERVICE_LIST_SOURCE.pageUrl,
    sourcePublicationDate: SAH_SERVICE_LIST_SOURCE.publicationDate,
    sourceSha256: SAH_SERVICE_LIST_SOURCE.docxSha256,
    clinicalCategoryFrom: item.clinicalCategoryFrom,
  };
}

export function isMdmServiceCataloguesEnabled(env?: Record<string, string | undefined>): boolean {
  const source =
    env ??
    (typeof process !== 'undefined'
      ? (process.env as Record<string, string | undefined>)
      : undefined);
  const value = source?.[MDM_SERVICE_CATALOGUES_FLAG];
  return value === 'true' || value === '1' || value === 'yes';
}

export function normaliseServiceTypeKey(value: string | null | undefined): string {
  return (value ?? '')
    .trim()
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

export function normaliseServiceCatalogueRow(
  row: MdmServiceCatalogueRow | MdmServiceCatalogueRpcRow,
): MdmServiceCatalogueRow {
  const raw = row as unknown as Record<string, unknown>;
  const serviceCode = stringFrom(raw, 'serviceCode', 'service_code');
  const serviceType = optionalStringFrom(raw, 'serviceType', 'service_type');
  const serviceCategory = optionalStringFrom(raw, 'serviceCategory', 'service_category');
  const unitType = optionalStringFrom(raw, 'unitType', 'unit_type');
  const fundingSourceCode = optionalStringFrom(raw, 'fundingSourceCode', 'funding_source_code');

  return {
    serviceCode,
    program: stringFrom(raw, 'program', 'program', 'SAH'),
    category: stringFrom(raw, 'category', 'category', 'OTHER'),
    displayName: stringFrom(raw, 'displayName', 'display_name'),
    description: nullableStringFrom(raw, 'description', 'description'),
    defaultUnitType: stringFrom(raw, 'defaultUnitType', 'default_unit_type', 'HOUR'),
    workerClassificationMinimum: nullableStringFrom(
      raw,
      'workerClassificationMinimum',
      'worker_classification_minimum',
    ),
    isActive: booleanFrom(raw, 'isActive', 'is_active', true),
    effectiveFrom: stringFrom(raw, 'effectiveFrom', 'effective_from'),
    effectiveTo: nullableStringFrom(raw, 'effectiveTo', 'effective_to'),
    tenantId: nullableStringFrom(raw, 'tenantId', 'tenant_id'),
    tenantDisplayName: nullableStringFrom(raw, 'tenantDisplayName', 'tenant_display_name'),
    tenantDescription: nullableStringFrom(raw, 'tenantDescription', 'tenant_description'),
    isEnabled: booleanFrom(raw, 'isEnabled', 'is_enabled', true),
    resolvedDisplayName:
      optionalStringFrom(raw, 'resolvedDisplayName', 'resolved_display_name') ||
      stringFrom(raw, 'displayName', 'display_name'),
    resolvedDescription:
      nullableStringFrom(raw, 'resolvedDescription', 'resolved_description') ??
      nullableStringFrom(raw, 'description', 'description'),
    serviceType: serviceType || deriveServiceTypeFromCode(serviceCode),
    serviceCategory: serviceCategory || mapMdmCategoryToSahServiceCategory(raw.category as string),
    unitType: unitType || undefined,
    fundingSourceCode: fundingSourceCode || undefined,
    sortOrder: numberFrom(raw, 'sortOrder', 'sort_order'),
    serviceTypeLabel: optionalStringFrom(raw, 'serviceTypeLabel', 'service_type_label'),
    sourceUrl: optionalStringFrom(raw, 'sourceUrl', 'source_url'),
    sourcePublicationDate: optionalStringFrom(
      raw,
      'sourcePublicationDate',
      'source_publication_date',
    ),
    sourceSha256: optionalStringFrom(raw, 'sourceSha256', 'source_sha256'),
    clinicalCategoryFrom: optionalStringFrom(raw, 'clinicalCategoryFrom', 'clinical_category_from'),
  };
}

export function normaliseServiceCatalogueRows(
  rows: readonly (MdmServiceCatalogueRow | MdmServiceCatalogueRpcRow)[],
): MdmServiceCatalogueRow[] {
  return rows.map(normaliseServiceCatalogueRow);
}

export function getFallbackServiceCatalogue(
  program: MdmServiceProgram | string = 'SAH',
  atDate: string = dateOnlyInTz(new Date()),
): MdmServiceCatalogueRow[] {
  if (program === 'NDIS') {
    const pkg = loadBundledNdisCatalogueSeed();
    return cloneRows(
      pkg.items.map((item) => {
        const row = ndisItemToMdmRow(item);
        return {
          serviceCode: row.service_code,
          program: 'NDIS',
          category: row.category,
          displayName: row.display_name,
          description: row.description,
          defaultUnitType: row.default_unit_type,
          workerClassificationMinimum: null,
          isActive: true,
          effectiveFrom: row.effective_from,
          effectiveTo: row.effective_to,
          serviceType: row.service_code,
          serviceCategory: row.category,
          unitType: row.default_unit_type,
          resolvedDisplayName: row.display_name,
        } satisfies MdmServiceCatalogueRow;
      }),
    );
  }
  if (program !== 'SAH') return [];
  return cloneRows(
    OFFICIAL_SAH_SERVICE_CATALOGUE.map((item) => officialSahFallbackRow(item, atDate)),
  );
}

export async function getServiceCatalogue(
  options: GetServiceCatalogueOptions = {},
): Promise<MdmServiceCatalogueLookupResult> {
  // An explicit null means the caller could not map this plan type to a
  // catalogue programme. Falling through to 'SAH' here would silently offer
  // Support at Home services on, say, a DVA plan, which is what
  // carePlanTypeToServiceCatalogueProgram returning null exists to prevent.
  // Fail visibly with an empty result and a reason instead.
  if (options.program === null) {
    return {
      enabled: options.enabled ?? isMdmServiceCataloguesEnabled(),
      source: 'fallback',
      rows: [],
      error: 'unmapped_program',
    };
  }
  const program = options.program ?? 'SAH';
  // Default to today's date resolved in Australia/Sydney (DEFAULT_TZ) rather
  // than UTC — `.toISOString().slice(0,10)` returns yesterday's calendar date
  // between 00:00–10:00 AEST, which can mis-classify "today" service catalogue
  // lookups during the early-morning window. Callers should still pass an
  // explicit atDate when tenant-tz is known.
  const atDate = options.atDate ?? dateOnlyInTz(new Date());
  const enabled = options.enabled ?? isMdmServiceCataloguesEnabled();
  const ttlMs = options.ttlMs ?? MDM_SERVICE_CATALOGUE_TTL_MS;
  const nowMs = options.nowMs ?? Date.now();

  if (!enabled || !options.fetcher) {
    return fallbackResult(
      enabled,
      program,
      !options.fetcher && enabled ? 'missing_fetcher' : undefined,
      atDate,
    );
  }

  const key =
    options.cacheKey ??
    `${options.tenantId ?? 'global'}:${program}:${atDate}:${MDM_SERVICE_CATALOGUES_FLAG}`;
  const cached = serviceCatalogueCache.get(key);
  if (cached && cached.expiresAt > nowMs) {
    return cloneResult(cached.result);
  }

  try {
    const rows = normaliseServiceCatalogueRows(
      await options.fetcher({ tenantId: options.tenantId, program, atDate }),
    );
    const activeRows = rows.filter((row) => row.isActive && row.isEnabled !== false);
    if (activeRows.length === 0) {
      return fallbackResult(true, program, 'empty_mdm_result', atDate);
    }

    const result: MdmServiceCatalogueLookupResult = {
      enabled: true,
      source: 'mdm',
      rows: sortCatalogueRows(activeRows),
    };
    serviceCatalogueCache.set(key, { expiresAt: nowMs + ttlMs, result: cloneResult(result) });
    return cloneResult(result);
  } catch (error) {
    return fallbackResult(
      true,
      program,
      error instanceof Error ? error.message : String(error),
      atDate,
    );
  }
}

export function clearServiceCatalogueCache(): void {
  serviceCatalogueCache.clear();
}

export function getServiceCatalogueOptions(
  rows: readonly (MdmServiceCatalogueRow | MdmServiceCatalogueRpcRow)[],
  atDate?: string,
): MdmServiceCatalogueOption[] {
  return sortCatalogueRows(normaliseServiceCatalogueRows(rows))
    .filter((row) => row.isActive && row.isEnabled !== false)
    .map((row) => {
      const serviceType = row.serviceType || deriveServiceTypeFromCode(row.serviceCode);
      const defaults = resolveSahServiceDefaults(serviceType, [row], atDate);
      return {
        value: serviceType,
        label: row.resolvedDisplayName || row.displayName,
        serviceCode: row.serviceCode,
        serviceCategory: defaults.serviceCategory,
        unitType: defaults.unitType,
        fundingSourceCode: defaults.fundingSourceCode,
      };
    });
}

export function resolveSahServiceDefaults(
  serviceTypeOrCode: string | null | undefined,
  rows: readonly (MdmServiceCatalogueRow | MdmServiceCatalogueRpcRow)[] = [],
  atDate?: string,
): SahServiceDefaults {
  const serviceType = normaliseServiceTypeKey(serviceTypeOrCode);
  const normalisedRows = normaliseServiceCatalogueRows(rows);
  const row = normalisedRows.find((candidate) =>
    serviceCatalogueRowMatches(candidate, serviceType),
  );
  const resolvedServiceType = row?.serviceType || serviceType;
  const category =
    row?.clinicalCategoryFrom && atDate && atDate >= row.clinicalCategoryFrom
      ? 'clinical'
      : (coerceSahServiceCategory(row?.serviceCategory) ??
        mapMdmCategoryToSahServiceCategory(row?.category) ??
        LEGACY_SERVICE_CATEGORY_DEFAULTS[resolvedServiceType] ??
        'everyday_living');
  const unitType =
    coerceSahUnitType(row?.unitType) ??
    mapMdmDefaultUnitType(row?.defaultUnitType, resolvedServiceType) ??
    LEGACY_UNIT_TYPE_DEFAULTS[resolvedServiceType] ??
    'hours';
  const fundingSourceCode =
    coerceSahFundingSourceCode(row?.fundingSourceCode) ??
    LEGACY_FUNDING_CODE_DEFAULTS[resolvedServiceType] ??
    'ON';

  return {
    serviceCode: row?.serviceCode ?? null,
    serviceType: resolvedServiceType,
    serviceCategory: category,
    unitType,
    fundingSourceCode,
    displayName: row?.resolvedDisplayName || row?.displayName || null,
  };
}

function stringFrom(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
  fallback = '',
): string {
  const value = raw[camelKey] ?? raw[snakeKey];
  return typeof value === 'string' ? value : fallback;
}

function optionalStringFrom(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): string | undefined {
  const value = stringFrom(raw, camelKey, snakeKey);
  return value || undefined;
}

function nullableStringFrom(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): string | null {
  const value = raw[camelKey] ?? raw[snakeKey];
  return typeof value === 'string' ? value : null;
}

function booleanFrom(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
  fallback: boolean,
): boolean {
  const value = raw[camelKey] ?? raw[snakeKey];
  return typeof value === 'boolean' ? value : fallback;
}

function numberFrom(
  raw: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): number | undefined {
  const value = raw[camelKey] ?? raw[snakeKey];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function fallbackResult(
  enabled: boolean,
  program: MdmServiceProgram | string,
  error?: string,
  atDate: string = dateOnlyInTz(new Date()),
): MdmServiceCatalogueLookupResult {
  return {
    enabled,
    source: 'fallback',
    rows: getFallbackServiceCatalogue(program, atDate),
    ...(error ? { error } : {}),
  };
}

function cloneResult(result: MdmServiceCatalogueLookupResult): MdmServiceCatalogueLookupResult {
  return {
    ...result,
    rows: cloneRows(result.rows),
  };
}

function cloneRows(rows: readonly MdmServiceCatalogueRow[]): MdmServiceCatalogueRow[] {
  return rows.map((row) => ({ ...row }));
}

function sortCatalogueRows(rows: readonly MdmServiceCatalogueRow[]): MdmServiceCatalogueRow[] {
  return cloneRows(rows).sort(
    (a, b) =>
      (a.sortOrder ?? 999) - (b.sortOrder ?? 999) ||
      (a.resolvedDisplayName || a.displayName).localeCompare(
        b.resolvedDisplayName || b.displayName,
      ),
  );
}

function serviceCatalogueRowMatches(row: MdmServiceCatalogueRow, serviceType: string): boolean {
  const candidates = [
    row.serviceType,
    row.serviceCode,
    row.displayName,
    row.resolvedDisplayName,
  ].filter((candidate): candidate is string => Boolean(candidate));
  return candidates.some((candidate) => normaliseServiceTypeKey(candidate) === serviceType);
}

function deriveServiceTypeFromCode(serviceCode: string): string {
  return normaliseServiceTypeKey(serviceCode.replace(/^SAH_/, ''));
}

function coerceSahServiceCategory(
  value: string | null | undefined,
): SahUiServiceCategory | undefined {
  if (value === 'clinical' || value === 'independence' || value === 'everyday_living') {
    return value;
  }
  return undefined;
}

function coerceSahUnitType(value: string | null | undefined): SahUiUnitType | undefined {
  if (
    value === 'hours' ||
    value === 'trip' ||
    value === 'item' ||
    value === 'bill' ||
    value === 'voucher' ||
    value === 'meal'
  ) {
    return value;
  }
  return undefined;
}

function coerceSahFundingSourceCode(
  value: string | null | undefined,
): SahFundingSourceCode | undefined {
  if (
    value === 'ON' ||
    value === 'CM' ||
    value === 'AT' ||
    value === 'HM' ||
    value === 'RC' ||
    value === 'EL'
  ) {
    return value;
  }
  return undefined;
}

function mapMdmCategoryToSahServiceCategory(
  value: string | null | undefined,
): SahUiServiceCategory | undefined {
  switch (value) {
    case 'CLINICAL_CARE':
    case 'CARE_MANAGEMENT':
      return 'clinical';
    case 'INDEPENDENCE':
    case 'AT_HM':
      return 'independence';
    case 'EVERYDAY_LIVING':
      return 'everyday_living';
    default:
      return undefined;
  }
}

function mapMdmDefaultUnitType(
  value: string | null | undefined,
  serviceType: string,
): SahUiUnitType | undefined {
  if (LEGACY_UNIT_TYPE_DEFAULTS[serviceType]) {
    return LEGACY_UNIT_TYPE_DEFAULTS[serviceType];
  }
  switch (value) {
    case 'HOUR':
      return 'hours';
    case 'KM':
      return 'trip';
    case 'VISIT':
      return 'item';
    case 'WEEK':
      return 'bill';
    default:
      return undefined;
  }
}
